import React, { useState, useEffect } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import aiService from '../services/ai-service';
import {
  SparklesIcon,
  ChartBarIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CameraIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  CalendarIcon,
  BoltIcon,
  ShieldCheckIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';

export default function AIDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeProject, setActiveProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [insights, setInsights] = useState({
    quotes: null,
    risks: [],
    predictions: null,
    quality: null,
    trends: null
  });
  const [selectedFeature, setSelectedFeature] = useState('overview');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeProject) {
      loadProjectInsights(activeProject);
    }
  }, [activeProject]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load projects first (faster)
      setLoading(false); // Show UI immediately
      const projectsSnapshot = await getDocs(
        query(collection(db, 'projects'), orderBy('createdAt', 'desc'), limit(10))
      );
      const projectsData = projectsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProjects(projectsData);
      
      if (projectsData.length > 0) {
        setActiveProject(projectsData[0].id);
      }
      
      // Load AI features lazily in background
      setTimeout(async () => {
        try {
          await aiService.initialize();
          const trends = await aiService.predictSeasonalTrends();
          setInsights(prev => ({ ...prev, trends }));
        } catch (error) {
          console.error('AI features unavailable:', error);
        }
      }, 100);
      
    } catch (error) {
      console.error('Error loading AI dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectInsights = async (projectId) => {
    try {
      // Load project risks
      const risks = await aiService.detectProjectRisks(projectId);
      
      // Load project quality assessment
      const quality = await aiService.assessProjectQuality(projectId);
      
      // Load compliance check
      const compliance = await aiService.checkCompliance(projectId);
      
      setInsights(prev => ({
        ...prev,
        risks,
        quality,
        compliance
      }));
      
    } catch (error) {
      console.error('Error loading project insights:', error);
    }
  };

  const analyzeQuotes = async (taskId) => {
    try {
      const analysis = await aiService.analyzeQuotes(activeProject, taskId);
      setInsights(prev => ({ ...prev, quotes: analysis }));
    } catch (error) {
      console.error('Error analyzing quotes:', error);
    }
  };

  const features = [
    {
      id: 'overview',
      name: 'AI Overview',
      icon: SparklesIcon,
      color: 'from-purple-400 to-pink-500'
    },
    {
      id: 'quotes',
      name: 'Quote Analysis',
      icon: ChartBarIcon,
      color: 'from-blue-400 to-indigo-500'
    },
    {
      id: 'predictions',
      name: 'Predictions',
      icon: ArrowTrendingUpIcon,
      color: 'from-green-400 to-emerald-500'
    },
    {
      id: 'automation',
      name: 'Automation',
      icon: BoltIcon,
      color: 'from-yellow-400 to-orange-500'
    },
    {
      id: 'quality',
      name: 'Quality Control',
      icon: ShieldCheckIcon,
      color: 'from-red-400 to-pink-500'
    },
    {
      id: 'vision',
      name: 'Visual AI',
      icon: CameraIcon,
      color: 'from-indigo-400 to-purple-500'
    }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* AI Score Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <BeakerIcon className="w-8 h-8 text-purple-600 mr-3" />
              <h3 className="text-lg font-bold text-gray-900">AI Intelligence Score</h3>
            </div>
          </div>
          <div className="text-center py-4">
            <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {insights.projects?.clientSatisfaction ? `${insights.projects.clientSatisfaction}%` : 'N/A'}
            </div>
            <p className="text-gray-600 mt-2">
              {insights.projects?.clientSatisfaction ? 'Client Satisfaction' : 'No ratings yet'}
            </p>
          </div>
          <div className="space-y-2 mt-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Pattern Recognition</span>
              <span className="font-medium">Active</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Predictive Models</span>
              <span className="font-medium">8 Active</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Automation Rules</span>
              <span className="font-medium">12 Active</span>
            </div>
          </div>
        </div>

        {/* Active Insights */}
        <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-6">
          <div className="flex items-center mb-4">
            <LightBulbIcon className="w-8 h-8 text-blue-600 mr-3" />
            <h3 className="text-lg font-bold text-gray-900">Active Insights</h3>
          </div>
          <div className="space-y-3">
            {insights.risks?.slice(0, 3).map((risk, index) => (
              <div key={index} className="flex items-start">
                <ExclamationTriangleIcon 
                  className={`w-5 h-5 mr-2 mt-0.5 ${
                    risk.severity === 'high' ? 'text-red-500' : 
                    risk.severity === 'medium' ? 'text-yellow-500' : 'text-blue-500'
                  }`} 
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{risk.message}</p>
                  <p className="text-xs text-gray-600">{risk.mitigation}</p>
                </div>
              </div>
            ))}
            {(!insights.risks || insights.risks.length === 0) && (
              <p className="text-gray-500 text-sm">No active risks detected</p>
            )}
          </div>
        </div>

        {/* Seasonal Trends */}
        <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-6">
          <div className="flex items-center mb-4">
            <CalendarIcon className="w-8 h-8 text-green-600 mr-3" />
            <h3 className="text-lg font-bold text-gray-900">Seasonal Insights</h3>
          </div>
          {insights.trends && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Current Period</p>
                <p className="text-lg font-bold text-green-600">{insights.trends.prediction}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Busy Months</p>
                <p className="text-sm text-gray-600">{insights.trends.busyPeriods?.join(', ')}</p>
              </div>
              {insights.trends.recommendation && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800">{insights.trends.recommendation}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* AI Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.slice(1).map(feature => (
          <button
            key={feature.id}
            onClick={() => setSelectedFeature(feature.id)}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 hover:shadow-2xl transition-all duration-300 text-left group"
          >
            <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${feature.color} mb-4`}>
              <feature.icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.name}</h3>
            <p className="text-sm text-gray-600">
              {feature.id === 'quotes' && 'Analyze and compare supplier quotes with AI insights'}
              {feature.id === 'predictions' && 'Predict timelines, budgets, and supplier performance'}
              {feature.id === 'automation' && 'Automate document generation and scheduling'}
              {feature.id === 'quality' && 'Monitor quality metrics and compliance'}
              {feature.id === 'vision' && 'Analyze project photos and track progress visually'}
            </p>
            <div className="mt-4 text-sm font-medium text-transparent bg-gradient-to-r bg-clip-text group-hover:from-purple-600 group-hover:to-pink-600 transition-all">
              Explore →
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderQuoteAnalysis = () => (
    <div className="space-y-6">
      {insights.quotes ? (
        <>
          {/* Price Analysis */}
          <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Price Analysis</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-600">Average</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${insights.quotes.priceAnalysis?.average.toFixed(0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Lowest</p>
                <p className="text-2xl font-bold text-green-600">
                  ${insights.quotes.priceAnalysis?.lowest.toFixed(0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Highest</p>
                <p className="text-2xl font-bold text-red-600">
                  ${insights.quotes.priceAnalysis?.highest.toFixed(0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Spread</p>
                <p className="text-2xl font-bold text-purple-600">
                  ${insights.quotes.priceAnalysis?.spread.toFixed(0)}
                </p>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">AI Recommendations</h3>
            <div className="space-y-3">
              {insights.quotes.recommendations?.map((rec, index) => (
                <div key={index} className="flex items-start p-3 bg-green-50 rounded-lg">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">{rec.supplierName}</p>
                    <p className="text-sm text-gray-600">{rec.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Warnings */}
          {insights.quotes.warnings?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-xl border border-red-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Warnings</h3>
              <div className="space-y-3">
                {insights.quotes.warnings.map((warning, index) => (
                  <div key={index} className="flex items-start p-3 bg-red-50 rounded-lg">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-3 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">{warning.supplierName}</p>
                      <p className="text-sm text-gray-600">{warning.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
          <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Quote Analysis Available</h3>
          <p className="text-gray-600 mb-4">Select a task with quotes to analyze</p>
          <button
            onClick={() => analyzeQuotes('sample-task-id')}
            className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
          >
            Analyze Sample Quotes
          </button>
        </div>
      )}
    </div>
  );

  const renderQualityControl = () => (
    <div className="space-y-6">
      {insights.quality && (
        <>
          {/* Quality Score */}
          <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Project Quality Assessment</h3>
            <div className="text-center py-6">
              <div className="relative inline-flex items-center justify-center">
                <div className="text-6xl font-bold text-gray-900">{insights.quality.overallScore}</div>
                <div className="absolute -right-8 -top-2 text-3xl font-bold text-green-600">
                  {insights.quality.grade}
                </div>
              </div>
              <p className="text-gray-600 mt-2">Overall Quality Score</p>
            </div>
            
            {/* Metrics */}
            <div className="space-y-3 mt-6">
              {Object.entries(insights.quality.metrics).map(([key, value]) => (
                <div key={key}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{Math.round(value)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        value >= 80 ? 'bg-green-500' : 
                        value >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${value}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Recommendations */}
            {insights.quality.recommendations?.length > 0 && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
                <ul className="space-y-1">
                  {insights.quality.recommendations.map((rec, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start">
                      <span className="mr-2">•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}

      {/* Compliance Check */}
      {insights.compliance && (
        <div className="bg-white rounded-2xl shadow-xl border border-yellow-100 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Compliance Status</h3>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <ShieldCheckIcon className={`w-8 h-8 mr-3 ${
                insights.compliance.compliant ? 'text-green-600' : 'text-yellow-600'
              }`} />
              <div>
                <p className="text-lg font-bold text-gray-900">
                  {insights.compliance.compliant ? 'Fully Compliant' : 'Issues Detected'}
                </p>
                <p className="text-sm text-gray-600">Score: {insights.compliance.score}%</p>
              </div>
            </div>
          </div>
          
          {insights.compliance.issues?.length > 0 && (
            <div className="space-y-2">
              {insights.compliance.issues.map((issue, index) => (
                <div key={index} className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{issue.type.replace(/_/g, ' ')}</p>
                  <p className="text-sm text-gray-600">{issue.action}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading AI insights...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 to-orange-400 rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-start">
            <SparklesIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">AI Intelligence Dashboard</h1>
              <p className="text-purple-100 text-xl">
                Powered by advanced machine learning to optimize your business
              </p>
              <div className="flex items-center space-x-6 text-purple-100 mt-4">
                <div className="flex items-center">
                  <BoltIcon className="w-5 h-5 mr-2" />
                  Real-time Analysis
                </div>
                <div className="flex items-center">
                  <ChartBarIcon className="w-5 h-5 mr-2" />
                  Predictive Insights
                </div>
                <div className="flex items-center">
                  <ShieldCheckIcon className="w-5 h-5 mr-2" />
                  Quality Monitoring
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
          {features.map(feature => (
            <button
              key={feature.id}
              onClick={() => setSelectedFeature(feature.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                selectedFeature === feature.id
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <feature.icon className="w-4 h-4 inline-block mr-2" />
              {feature.name}
            </button>
          ))}
        </div>

        {/* Content */}
        {selectedFeature === 'overview' && renderOverview()}
        {selectedFeature === 'quotes' && renderQuoteAnalysis()}
        {selectedFeature === 'quality' && renderQualityControl()}
        
        {/* Placeholder for other features */}
        {['predictions', 'automation', 'vision'].includes(selectedFeature) && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
            {(() => {
              const feature = features.find(f => f.id === selectedFeature);
              const Icon = feature?.icon;
              return Icon ? <Icon className="w-16 h-16 text-gray-400 mx-auto mb-4" /> : null;
            })()}
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {features.find(f => f.id === selectedFeature)?.name} Coming Soon
            </h3>
            <p className="text-gray-600">This AI feature is being trained and will be available shortly</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}